import React from 'react'
import PropTypes from 'prop-types'
import { Bar } from 'react-chartjs-2'
import _ from 'lodash'
import { chartjsTitleProperties } from '../../../common/Constants'

const complaintsIndicatorPlugin = {
  id: 'complaintsIndicator',
  afterDatasetsDraw: (chart, _args, { colors }) => {
    if (!colors?.length) return

    const { ctx, chartArea: { left, right, top, bottom }, scales: { x, y } } = chart
    const categoryWidth = x.ticks.length > 1
      ? Math.abs(x.getPixelForValue(1) - x.getPixelForValue(0))
      : right - left
    const yPixel = Math.min(Math.max(y.getPixelForValue(0), top), bottom)

    const lineWidth = 5

    ctx.save()
    ctx.beginPath()
    ctx.rect(left, top - lineWidth / 2, right - left, bottom - top + lineWidth)
    ctx.clip()
    ctx.setLineDash([])
    ctx.lineCap = 'butt'
    ctx.globalAlpha = 1
    ctx.lineWidth = lineWidth
    colors.forEach((color, index) => {
      const center = x.getPixelForValue(index)
      ctx.strokeStyle = color
      ctx.beginPath()
      ctx.moveTo(center - categoryWidth / 2, yPixel)
      ctx.lineTo(center + categoryWidth / 2, yPixel)
      ctx.stroke()
    })
    ctx.restore()
  }
}

const GroupedBarsChart = (props) => {
  const {
    entries,
    paramProperties,
    legendPosition = 'top',
    legendWidth = 40,
    legendEnabled = true,
    suggestedMin,
    suggestedMax,
    title,
    yLabel,
    stacked,
    tooltipCallbacks,
    xAxis,
    yAxis,
    barColors
  } = props

  const getAllParameters = (entries) => {
    const parameters = _.reduce(entries, (params, entry) => {
      entry.bars.forEach((bar) => params.add(bar.param))
      return params
    }, new Set())

    return Array.from(parameters)
  }

  const getChartJsData = (entries, paramProperties) => {
    const parameters = getAllParameters(entries)
    const datasets = _.reduce(parameters, (startingDatasets, param) => {
      startingDatasets[param] = []
      return startingDatasets
    }, {})
    const labels = []

    entries.forEach((entry) => {
      const { bars, label: entryLabel } = entry
      labels.push(entryLabel)

      _.forOwn(datasets, (values) => values.push(0)) // Fill the latest values with a 0
      bars.forEach((bar) => { // Save the values of the parameters that are not 0
        const { value, param } = bar
        const lastIndex = datasets[param].length - 1
        datasets[param][lastIndex] = value
      })
    })

    const datasetsExpanded = _.entries(datasets).map(([param, values]) => {
      return {
        label: paramProperties[param]?.title || param,
        backgroundColor: paramProperties[param]?.color,
        data: values
      }
    })

    return {
      labels,
      datasets: datasetsExpanded
    }
  }

  const options = {
    plugins: {
      legend: {
        display: !legendEnabled,
        position: legendPosition,
        labels: {
          boxWidth: legendWidth
        }
      },
      title: {
        text: title,
        ...chartjsTitleProperties
      },
      complaintsIndicator: {
        colors: barColors
      }
    },
    scales: {
      x: {
        stacked,
        ...xAxis
      },
      y: {
        stacked,
        suggestedMin,
        suggestedMax,
        title: {
          display: !!yLabel,
          text: yLabel
        },
        ...yAxis
      }
    }
  }
  if (tooltipCallbacks) {
    _.merge(options, {
      plugins: {
        tooltip: {
          callbacks: tooltipCallbacks
        }
      }
    })
  }

  const data = getChartJsData(entries, paramProperties)

  return (
    <Bar data={data} options={options} plugins={[complaintsIndicatorPlugin]} />
  )
}

GroupedBarsChart.propTypes = {
  // The options and properties of each parameter
  paramProperties: PropTypes.objectOf(PropTypes.shape({
    color: PropTypes.string,
    title: PropTypes.string
  })),
  // The data entries. Each entry is a group of several bars with a matching label
  entries: PropTypes.arrayOf(PropTypes.shape({
    // The bars of an entry. Each bar represents the value of a parameter for that entry's label
    bars: PropTypes.arrayOf(PropTypes.shape({
      param: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired
    })).isRequired,
    // The entry's label
    label: PropTypes.any.isRequired
  })).isRequired,
  options: PropTypes.object
}

export default GroupedBarsChart
